/**
 * Streaming chat with an A2A platform agent, over the platform-api SSE relay.
 *
 * Frame types: start, status, tool_call, tool_result, text, artifact,
 * decision, done, error. `: keepalive` comments reset the read timeout.
 */

import { getFreshToken } from '@/utils/auth-token-provider';
import { isRunInitiatingTool } from '@/services/agents/run-events-stream';
import * as Sentry from '@sentry/react';

export interface AgentChatArtifact {
  file_id?: string;
  filename?: string;
  file_name?: string;
  download_url?: string;
  content_type?: string;
  size_bytes?: number;
  expires_at?: string;
  edited?: boolean;
  version?: number;
  parent_file_id?: string;
  root_file_id?: string;
  source_run_id?: string;
  label?: string;
  change_summary?: string;
  artifact_id?: string;
  cross_run?: boolean;
  composed_from_run_ids?: string[];
  title?: string;
  artifact_type?: string;
  thumbnail_status?: string;
}

/**
 * A structured clarifying question the agent ended a turn on: the user
 * picks an option (or types their own) and the answer rides back on the
 * next turn's `metadata.decision_response`.
 */
export interface AgentChatDecision {
  decisionId: string;
  question: string;
  options: string[];
  allowFreeText: boolean;
}

/**
 * Parses a decision payload from either wire: the live SSE frame's fields
 * sit at the top level, and the persisted copy in message metadata has the
 * same shape -- one parser for both keeps the two from silently diverging
 * on what counts as a valid question.
 */
export function parseDecision(raw: unknown): AgentChatDecision | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const d = raw as Record<string, unknown>;
  const options = Array.isArray(d.options)
    ? d.options.filter((o): o is string => typeof o === 'string' && !!o.trim())
    : [];
  const question = (d.question as string) || (d.text as string) || '';
  // Half a decision is worse than none: an options-less panel would
  // replace the composer with nothing the user can answer.
  if (typeof d.decision_id !== 'string' || !question || !options.length) {
    return undefined;
  }
  return {
    decisionId: d.decision_id,
    question,
    options,
    allowFreeText: d.allow_free_text !== false,
  };
}

/**
 * One structural change proposed by the agent for the workflow canvas
 * (Wire 1.0.26). Mirrors the WS mutation vocabulary: `add_node`,
 * `remove_node`, `configure_node`, `connect_nodes`, `disconnect_nodes`.
 */
export interface AgentChatCanvasMutation {
  event_type: string;
  [key: string]: unknown;
}

/** The user's answer to one, as sent back on the next turn. */
export interface AgentChatDecisionResponse {
  decision_id: string;
  option_index?: number;
  option_label?: string;
  free_text?: string;
}

export interface AgentChatActivityEvent {
  type: 'status' | 'tool_call' | 'tool_result';
  text?: string;
  toolName?: string;
  /** Client-side receive time (ms epoch), set when the frame arrives. */
  at?: number;
}

export interface StreamAgentChatOptions {
  orgId: string;
  agentId: string;
  message: string;
  conversationId?: string | null;
  metadata?: Record<string, unknown>;
  signal?: AbortSignal;
  /** Fired once with the resolved conversation id (first frame). */
  onStart?: (conversationId: string) => void;
  /** Lifecycle / tool-call activity frames as the orchestrator works. */
  onActivity?: (event: AgentChatActivityEvent) => void;
  /** Extended-thinking text delta (streams while the model reasons). */
  onThinking?: (delta: string) => void;
  /** Fired as soon as a workflow-starting tool creates its run. */
  onRunStarted?: (runId: string) => void;
  /** The workflow a turn looked up, when the turn is about exactly one. */
  onWorkflowDescribed?: (workflowId: string) => void;
  /** A question the user answers by choosing rather than by typing. */
  onDecision?: (decision: AgentChatDecision) => void;
  /** Assistant text delta. */
  onToken?: (token: string) => void;
  /** A file reference discovered mid-turn (e.g. generated Word doc). */
  onArtifact?: (artifact: AgentChatArtifact) => void;
  /**
   * A canvas mutation proposed by the agent (Wire 1.0.26). Fired once
   * per frame; caller buffers them during the turn and flushes on done.
   */
  onMutation?: (mutation: AgentChatCanvasMutation) => void;
  /**
   * The agent requested a canvas run (Wire 1.0.27). Called when a
   * `canvas_run_workflow` frame arrives; the caller dispatches the run.
   */
  onRunWorkflow?: () => void;
  onDone: (result: {
    fullMessage: string;
    conversationId: string;
    artifacts: AgentChatArtifact[];
    workflowRunId?: string;
    workflowId?: string;
  }) => void;
  onError: (error: string) => void;
}

const READ_TIMEOUT_MS = 45_000;

export async function streamAgentChat(
  opts: StreamAgentChatOptions,
): Promise<void> {
  const {
    orgId,
    agentId,
    message,
    conversationId,
    metadata,
    signal,
    onStart,
    onActivity,
    onThinking,
    onRunStarted,
    onWorkflowDescribed,
    onDecision,
    onToken,
    onArtifact,
    onMutation,
    onRunWorkflow,
    onDone,
    onError,
  } = opts;

  const fail = (message: string, extra?: Record<string, unknown>) => {
    Sentry.captureException(new Error(message), {
      extra: { context: 'stream-agent-chat', orgId, agentId, ...extra },
    });
    onError(message);
  };

  const platformToken = await getFreshToken();
  if (!platformToken) {
    onError('Not authenticated');
    return;
  }

  let env = 'prod';
  if (typeof window !== 'undefined' && window.location) {
    const searchParams = new URLSearchParams(window.location.search);
    env = searchParams.get('env')?.toLowerCase() || 'prod';
  }

  const url = `/proxy/platform/agents/${encodeURIComponent(orgId)}/${encodeURIComponent(agentId)}/chat/stream?env=${env}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Token': platformToken,
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId ?? null,
        metadata: metadata ?? {},
      }),
      signal,
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      fail(`Chat failed: ${response.status} ${errText.slice(0, 200)}`, {
        status: response.status,
      });
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      fail('Streaming not supported in this browser');
      return;
    }

    const decoder = new TextDecoder();
    let buf = '';
    let fullMessage = '';
    let resolvedConversationId = conversationId || '';
    const artifacts: AgentChatArtifact[] = [];
    let terminal = false;

    /** Returns true when a terminal frame (done/error) was handled. */
    const handleLine = (line: string): boolean => {
      if (!line.startsWith('data: ')) return false;
      const raw = line.slice(6).trim();
      if (!raw) return false;

      let d: Record<string, unknown>;
      try {
        d = JSON.parse(raw);
      } catch {
        return false;
      }

      switch (d.type) {
        case 'start':
          if (typeof d.conversation_id === 'string' && d.conversation_id) {
            resolvedConversationId = d.conversation_id;
            onStart?.(resolvedConversationId);
          }
          return false;
        case 'status':
          onActivity?.({ type: 'status', text: (d.text as string) || '' });
          return false;
        case 'thinking': {
          const delta = (d.text as string) || '';
          if (delta) onThinking?.(delta);
          return false;
        }
        case 'tool_call':
          onActivity?.({
            type: 'tool_call',
            toolName: (d.tool_name as string) || '',
            text: (d.args as string) || '',
          });
          return false;
        case 'tool_result':
          onActivity?.({
            type: 'tool_result',
            toolName: (d.tool_name as string) || '',
            text: (d.content as string) || '',
          });
          // A structured run_id field, not just prose, so this is not the
          // same guard as extractRunIdFromText -- but it needs the same
          // tool-name check, or an inspection tool that also echoes run_id
          // structurally still attaches a run card for a run it only looked
          // up.
          if (
            typeof d.run_id === 'string' &&
            d.run_id &&
            isRunInitiatingTool(d.tool_name as string | undefined)
          ) {
            onRunStarted?.(d.run_id);
          }
          if (typeof d.workflow_id === 'string' && d.workflow_id) {
            onWorkflowDescribed?.(d.workflow_id);
          }
          return false;
        case 'run_started':
          if (typeof d.run_id === 'string' && d.run_id) {
            onRunStarted?.(d.run_id);
          }
          return false;
        case 'text': {
          const token = (d.text as string) || '';
          if (token) {
            fullMessage += token;
            onToken?.(token);
          }
          return false;
        }
        case 'artifact': {
          const artifact = (d.artifact ?? {}) as AgentChatArtifact;
          artifacts.push(artifact);
          onArtifact?.(artifact);
          return false;
        }
        case 'canvas_mutation': {
          const eventType = d.event_type as string | undefined;
          if (eventType) {
            onMutation?.(d as AgentChatCanvasMutation);
          }
          return false;
        }
        case 'canvas_run_workflow': {
          onRunWorkflow?.();
          return false;
        }
        case 'decision': {
          const decision = parseDecision(d);
          if (decision) onDecision?.(decision);
          return false;
        }
        case 'done': {
          if (typeof d.conversation_id === 'string' && d.conversation_id) {
            resolvedConversationId = d.conversation_id;
          }
          const reply = (d.assistant_reply as string) || fullMessage;
          const doneArtifacts = Array.isArray(d.artifacts)
            ? (d.artifacts as AgentChatArtifact[])
            : artifacts;
          onDone({
            fullMessage: reply.trim(),
            conversationId: resolvedConversationId,
            artifacts: doneArtifacts,
            workflowRunId:
              typeof d.workflow_run_id === 'string'
                ? d.workflow_run_id
                : undefined,
            workflowId:
              typeof d.workflow_id === 'string' ? d.workflow_id : undefined,
          });
          return true;
        }
        case 'error':
          onError((d.error as string) || 'Agent stream failed');
          return true;
        default:
          return false;
      }
    };

    while (!terminal) {
      const readPromise = reader.read();
      const timeoutPromise = new Promise<'timeout'>((resolve) =>
        setTimeout(() => resolve('timeout'), READ_TIMEOUT_MS),
      );
      const result = await Promise.race([readPromise, timeoutPromise]);

      if (result === 'timeout') {
        reader.cancel().catch(() => undefined);
        fail('Connection timed out waiting for the agent');
        return;
      }

      const { done, value } = result;
      if (done) break;

      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() || '';

      for (const line of lines) {
        // Keepalive comments — connection alive, nothing to process.
        if (line.startsWith(':')) continue;
        if (handleLine(line)) {
          terminal = true;
          break;
        }
      }
    }

    if (!terminal) {
      // Stream closed without a terminal frame.
      if (fullMessage) {
        onDone({
          fullMessage: fullMessage.trim(),
          conversationId: resolvedConversationId,
          artifacts,
        });
      } else {
        fail('Agent stream ended unexpectedly without a reply');
      }
    }
  } catch (err) {
    if (signal?.aborted) return;
    Sentry.captureException(err, {
      extra: { context: 'stream-agent-chat', orgId, agentId },
    });
    onError(err instanceof Error ? err.message : String(err));
  }
}
